/**
 * Shared helpers for invoice Total / Due on Invoice Details (mirrors BE dealAmounts).
 */

export const INVOICE_SOURCE_DEBIT_NOTE = 'Debit Note';
export const INVOICE_SOURCE_CREDIT_NOTE = 'Credit Note';
export const INVOICE_SOURCE_EXTRA_CHARGE = 'Extra Charge';

export const ADJUSTMENT_REASON_PRICE_CORRECTION = 'price_correction';
export const ADJUSTMENT_REASON_COMMERCIAL_WRITE_OFF = 'commercial_write_off';

/** Coerce to a finite number; junk (NaN / Infinity / non-numeric) → fallback. */
export function finiteNumber(value, fallback = 0) {
  const n = Number(value);
  return Number.isFinite(n) ? n : fallback;
}

export function isActiveInvoice(invoice) {
  return !invoice?.cancelled && invoice?.status !== 'Cancelled';
}

export function isCreditNoteInvoice(invoice) {
  return String(invoice?.source || '').trim() === INVOICE_SOURCE_CREDIT_NOTE;
}

export function isDebitNoteInvoice(invoice) {
  return String(invoice?.source || '').trim() === INVOICE_SOURCE_DEBIT_NOTE;
}

/** Late-fee invoices are additive dues, not part of the sale-price stage schedule. */
export function isLateFeeInvoice(invoice) {
  const source = String(invoice?.source || '')
    .trim()
    .toLowerCase()
    .replace(/\s+/g, '');
  return source === 'latefee' || source === 'latefees';
}

/**
 * Price-correction CN (PC/…) settles an over-billed invoice line.
 * Must NOT shrink closed-sale / Total Price (blueprint).
 */
export function isPriceCorrectionCreditNote(invoice) {
  if (!isCreditNoteInvoice(invoice)) return false;
  return String(invoice?.invoiceNumber || '')
    .trim()
    .toUpperCase()
    .startsWith('PC/');
}

/** Commercial write-off CN (WO/…) — may reduce adjusted total. */
export function isCommercialWriteOffCreditNote(invoice) {
  if (!isCreditNoteInvoice(invoice)) return false;
  return String(invoice?.invoiceNumber || '')
    .trim()
    .toUpperCase()
    .startsWith('WO/');
}

/** PC/{dealId}/{targetInvoiceId}/{ts} → target id; else infer by matching open due. */
export function getPriceCorrectionTargetInvoiceId(creditNote, invoices = []) {
  if (!isPriceCorrectionCreditNote(creditNote)) return null;
  const parts = String(creditNote?.invoiceNumber || '')
    .trim()
    .split('/');
  if (parts.length >= 4) {
    const targetId = Number(parts[2]);
    if (
      Number.isFinite(targetId) &&
      invoices.some((inv) => Number(inv?.id) === targetId)
    ) {
      return targetId;
    }
  }
  const cnAmt = finiteNumber(creditNote?.amount);
  const candidates = invoices
    .filter(
      (inv) =>
        isActiveInvoice(inv) &&
        !isCreditNoteInvoice(inv) &&
        !isLateFeeInvoice(inv)
    )
    .map((inv) => ({
      id: Number(inv.id),
      due: Math.max(
        0,
        finiteNumber(inv.amount) - finiteNumber(inv.paid_amount)
      )
    }))
    .filter((c) => c.due > 0);
  const exact = candidates.find((c) => c.due === cnAmt);
  if (exact) return exact.id;
  candidates.sort((a, b) => b.due - a.due);
  return candidates[0]?.id ?? null;
}

export function priceCorrectionAppliedToInvoice(invoiceId, invoices = []) {
  if (invoiceId == null) return 0;
  return invoices.reduce((sum, inv) => {
    if (!isActiveInvoice(inv) || !isPriceCorrectionCreditNote(inv)) return sum;
    const target = getPriceCorrectionTargetInvoiceId(inv, invoices);
    if (Number(target) !== Number(invoiceId)) return sum;
    return sum + finiteNumber(inv.amount);
  }, 0);
}

export function buildPriceCorrectionInvoiceNumber(dealId, targetInvoiceId) {
  return `PC/${dealId}/${targetInvoiceId}/${Date.now()}`;
}

export function pickPriceCorrectionTargetInvoice(invoices = []) {
  const candidates = invoices
    .filter(
      (inv) =>
        isActiveInvoice(inv) &&
        !isCreditNoteInvoice(inv) &&
        !isLateFeeInvoice(inv)
    )
    .map((inv) => ({
      invoice: inv,
      due: Math.max(
        0,
        finiteNumber(inv.amount) +
          finiteNumber(inv.late_fees) -
          finiteNumber(inv.paid_amount) -
          finiteNumber(inv.paid_late_fees)
      )
    }))
    .filter((c) => c.due > 0)
    .sort((a, b) => b.due - a.due);
  return candidates[0]?.invoice ?? null;
}

/**
 * @param {object} invoice
 * @param {object[]} [invoices] full deal set — needed so PC CNs settle the target line
 */
export function invoiceOutstandingContribution(invoice, invoices = null) {
  if (!isActiveInvoice(invoice)) return 0;
  const amount = finiteNumber(invoice?.amount);
  const late = finiteNumber(invoice?.late_fees);
  const paid = finiteNumber(invoice?.paid_amount);
  const paidLate = finiteNumber(invoice?.paid_late_fees);
  // Price-correction CN settles the target invoice line; it is not a deal-total cut.
  if (isPriceCorrectionCreditNote(invoice)) return 0;
  if (isCreditNoteInvoice(invoice)) {
    return -amount;
  }
  const raw = Math.max(0, amount + late - paid - paidLate);
  if (!Array.isArray(invoices)) return raw;
  const applied = priceCorrectionAppliedToInvoice(invoice?.id, invoices);
  return Math.max(0, raw - applied);
}

export function invoiceTotalAdjustmentContribution(invoice) {
  if (!isActiveInvoice(invoice)) return 0;
  const amount = finiteNumber(invoice?.amount);
  // Closed sale is immutable for price-correction CNs.
  if (isPriceCorrectionCreditNote(invoice)) return 0;
  if (isCreditNoteInvoice(invoice)) return -amount;
  const source = String(invoice?.source || '').trim();
  if (source === INVOICE_SOURCE_DEBIT_NOTE || source === INVOICE_SOURCE_EXTRA_CHARGE) {
    return amount;
  }
  return 0;
}

/** Active invoice principal covering the sale schedule (excludes CN + latefee rows). */
export function invoiceSaleScheduleContribution(invoice) {
  if (!isActiveInvoice(invoice)) return 0;
  if (isCreditNoteInvoice(invoice)) return 0;
  if (isLateFeeInvoice(invoice)) return 0;
  return Math.max(0, finiteNumber(invoice?.amount));
}

export function computeInvoiceOutstandingDue(invoices = []) {
  const due = invoices.reduce(
    (sum, inv) => sum + invoiceOutstandingContribution(inv, invoices),
    0
  );
  return Math.max(0, finiteNumber(due));
}

export function computeUninvoicedSaleAmount(baseTotalPrice, invoices = []) {
  const adjustedTotal = computeAdjustedDealTotal(baseTotalPrice, invoices);
  const scheduled = invoices.reduce(
    (sum, inv) => sum + invoiceSaleScheduleContribution(inv),
    0
  );
  return Math.max(0, adjustedTotal - scheduled);
}

/**
 * Open invoice balance (gross receivable):
 * unpaid invoices + uninvoiced sale remainder after cash/CN/write-off on lines.
 * Does NOT subtract cash credit wallet — that is net-to-collect.
 */
export function computeOpenInvoiceBalance(baseTotalPrice, invoices = []) {
  const invoiceDue = computeInvoiceOutstandingDue(invoices);
  if (baseTotalPrice == null || !Number.isFinite(Number(baseTotalPrice))) {
    return Math.max(0, Math.round(invoiceDue));
  }
  const uninvoiced = computeUninvoicedSaleAmount(Number(baseTotalPrice), invoices);
  return Math.max(0, Math.round(finiteNumber(invoiceDue + uninvoiced)));
}

/** Net amount to collect after applying unused cash credit wallet. */
export function computeNetAmountToCollect(openInvoiceBalance, cashCreditBalance = 0) {
  const open = Math.max(0, finiteNumber(openInvoiceBalance));
  const credit = Math.max(0, finiteNumber(cashCreditBalance));
  return Math.max(0, Math.round(open - credit));
}

/**
 * Commercial credit notes applied on this deal (PC/WO/other CN amounts).
 * These settle invoice lines; they are NOT cash credit / refundable wallet.
 */
export function computeCommercialCreditApplied(invoices = []) {
  return Math.max(
    0,
    Math.round(
      invoices.reduce((sum, inv) => {
        if (!isActiveInvoice(inv) || !isCreditNoteInvoice(inv)) return sum;
        return sum + finiteNumber(inv?.amount);
      }, 0)
    )
  );
}

/**
 * Deal due vs sale: open invoice balance − cash credit (= net amount to collect).
 * Pass baseTotalPrice / creditBalance when available (Invoice Details / deal list).
 */
export function computeDealDueFromInvoices(invoices = [], options = {}) {
  const open = computeOpenInvoiceBalance(options?.baseTotalPrice, invoices);
  const credit = Math.max(0, finiteNumber(options?.creditBalance));
  return computeNetAmountToCollect(open, credit);
}

/** Alias — net amount to collect (open − cash credit). */
export function computeDealRemainingDue(
  baseTotalPrice,
  invoices = [],
  creditBalance = 0
) {
  return computeDealDueFromInvoices(invoices, {
    baseTotalPrice,
    creditBalance
  });
}

/** Paid principal + late fees on active invoices only (excludes cancelled / credit notes). */
export function computeDealPaidFromInvoices(invoices = []) {
  return invoices.reduce((sum, inv) => {
    if (!isActiveInvoice(inv) || isCreditNoteInvoice(inv)) return sum;
    return sum + finiteNumber(inv?.paid_amount) + finiteNumber(inv?.paid_late_fees);
  }, 0);
}

export function computeAdjustedDealTotal(baseTotalPrice, invoices = []) {
  const adjustment = invoices.reduce(
    (sum, inv) => sum + invoiceTotalAdjustmentContribution(inv),
    0
  );
  return Math.max(0, Math.round(finiteNumber(baseTotalPrice) + adjustment));
}

/**
 * Sale remaining only (excludes late-fee outstanding) — used for balance write-off.
 * = uninvoiced sale + unpaid stage principal − credit
 */
export function computeSaleRemainingBalance(
  baseTotalPrice,
  invoices = [],
  creditBalance = 0
) {
  const uninvoiced = computeUninvoicedSaleAmount(baseTotalPrice, invoices);
  const unpaidPrincipal = invoices.reduce((sum, inv) => {
    if (
      !isActiveInvoice(inv) ||
      isCreditNoteInvoice(inv) ||
      isLateFeeInvoice(inv)
    ) {
      return sum;
    }
    // Line open after price-correction applications (principal only for write-off size).
    const raw = Math.max(
      0,
      finiteNumber(inv?.amount) - finiteNumber(inv?.paid_amount)
    );
    const applied = priceCorrectionAppliedToInvoice(inv?.id, invoices);
    return sum + Math.max(0, raw - applied);
  }, 0);
  const credit = Math.max(0, finiteNumber(creditBalance));
  return Math.max(0, Math.round(uninvoiced + unpaidPrincipal - credit));
}

/**
 * Outstanding on late-fee invoices only (and late_fees line on stage invoices).
 * Additive on top of sale; does not consume deal credit.
 */
export function computeLateFeeOutstandingDue(invoices = []) {
  const due = invoices.reduce((sum, inv) => {
    if (!isActiveInvoice(inv) || isCreditNoteInvoice(inv)) return sum;
    if (isLateFeeInvoice(inv)) {
      return sum + invoiceOutstandingContribution(inv, invoices);
    }
    // Stage rows can still carry an on-invoice late_fees balance
    const late = finiteNumber(inv?.late_fees);
    const paidLate = finiteNumber(inv?.paid_late_fees);
    return sum + Math.max(0, late - paidLate);
  }, 0);
  return Math.max(0, Math.round(finiteNumber(due)));
}

/** Gross stage schedule − active price-correction CNs (net billable schedule). */
export function computeNetSaleScheduleTotal(invoices = []) {
  const gross = computeSaleScheduleTotal(invoices);
  const pc = invoices.reduce((sum, inv) => {
    if (!isActiveInvoice(inv) || !isPriceCorrectionCreditNote(inv)) return sum;
    return sum + finiteNumber(inv.amount);
  }, 0);
  return Math.max(0, Math.round(gross - pc));
}

/**
 * UI breakdown so Due is not read as Total − Paid alone.
 * totalDue === saleDue + lateFeeDue (same total as computeDealRemainingDue).
 */
export function computeDealDueBreakdown(
  baseTotalPrice,
  invoices = [],
  creditBalance = 0
) {
  const openInvoiceBalance = computeOpenInvoiceBalance(baseTotalPrice, invoices);
  const cashCreditBalance = Math.max(0, finiteNumber(creditBalance));
  const saleDue = computeSaleRemainingBalance(
    baseTotalPrice,
    invoices,
    creditBalance
  );
  const lateFeeDue = computeLateFeeOutstandingDue(invoices);
  const totalDue = computeNetAmountToCollect(
    openInvoiceBalance,
    cashCreditBalance
  );
  return {
    openInvoiceBalance,
    cashCreditBalance,
    netAmountToCollect: totalDue,
    commercialCreditApplied: computeCommercialCreditApplied(invoices),
    saleDue,
    lateFeeDue,
    totalDue,
    paid: computeDealPaidFromInvoices(invoices),
    totalPrice: computeAdjustedDealTotal(baseTotalPrice, invoices),
    closedSale: Math.round(finiteNumber(baseTotalPrice)),
    netSchedule: computeNetSaleScheduleTotal(invoices)
  };
}

export function computeSaleScheduleTotal(invoices = []) {
  return Math.max(
    0,
    Math.round(
      invoices.reduce((sum, inv) => sum + invoiceSaleScheduleContribution(inv), 0)
    )
  );
}

/**
 * True when a history row is credit-wallet allocation, not a bank receipt.
 * These must not inflate Cash Received.
 */
export function isCreditAllocationHistoryRow(row) {
  const remarks = String(row?.remarks || '')
    .trim()
    .toLowerCase();
  const source = String(row?.source || '')
    .trim()
    .toLowerCase();
  const mode = String(row?.mode || '')
    .trim()
    .toLowerCase();
  const txn = String(row?.transactionID || row?.transactionId || '')
    .trim()
    .toUpperCase();
  if (source === 'credit_balance' || source === 'credit-balance') return true;
  if (mode.includes('credit')) return true;
  if (txn.startsWith('CREDIT_AUTO_')) return true;
  return (
    remarks.includes('auto-applied from existing credit') ||
    remarks.includes('auto-applied from credit') ||
    remarks.includes('applied from existing credit') ||
    remarks.includes('from credit balance') ||
    remarks.includes('applied from credit balance')
  );
}

/** Display alias: historic `nft` → NEFT. */
export function normalizePaymentModeDisplay(mode) {
  const m = String(mode || '')
    .trim()
    .toLowerCase();
  if (m === 'nft' || m === 'neft') return 'NEFT';
  return String(mode || '').trim() || '—';
}

export function normalizePaymentModeWrite(mode) {
  const m = String(mode || '')
    .trim()
    .toLowerCase();
  if (m === 'nft' || m === 'neft') return 'neft';
  return m;
}

/**
 * Verified receipts only: Credited − Debited, excluding credit-allocation rows.
 */
export function computeCashReceivedFromHistory(history = []) {
  let net = 0;
  for (const row of history) {
    if (isCreditAllocationHistoryRow(row)) continue;
    const amt = finiteNumber(row?.amount_paid);
    const t = String(row?.type || '')
      .trim()
      .toLowerCase();
    if (t === 'debited' || t === 'debit') net -= amt;
    else net += amt;
  }
  return Math.max(0, Math.round(net));
}

/**
 * Reconcile snapshot (mirrors BE dealAmounts.computeDealReconcileSnapshot).
 * open_invoice_balance ≠ net_amount_to_collect (credit reduces net only).
 */
export function computeDealReconcileSnapshot({
  baseTotalPrice,
  invoices = [],
  creditBalance = 0,
  verifiedCashCredit = null,
  legacyUnverifiedCredit = null,
  cashReceived = 0,
  refundsAndTransfersOut = 0,
  tallyLedgerBalance = null
} = {}) {
  const base = finiteNumber(baseTotalPrice);
  const verified = Math.max(
    0,
    finiteNumber(
      verifiedCashCredit != null
        ? verifiedCashCredit
        : 0
    )
  );
  const legacyUnverified = Math.max(
    0,
    finiteNumber(
      legacyUnverifiedCredit != null
        ? legacyUnverifiedCredit
        : verifiedCashCredit == null
          ? creditBalance
          : 0
    )
  );
  const cash = Math.max(0, Math.round(finiteNumber(cashReceived)));
  const refundsOut = Math.max(0, Math.round(finiteNumber(refundsAndTransfersOut)));
  const adjusted = computeAdjustedDealTotal(base, invoices);
  const schedule = computeSaleScheduleTotal(invoices);
  const net_schedule = computeNetSaleScheduleTotal(invoices);
  // Overage vs closed sale after price-correction CNs are applied.
  const pricing_overage = Math.max(0, net_schedule - Math.round(base));
  const allocated = computeDealPaidFromInvoices(invoices);
  const open = computeOpenInvoiceBalance(base, invoices);
  // Net uses VERIFIED cash credit only.
  const netToCollect = computeNetAmountToCollect(open, verified);
  const commercialCredit = computeCommercialCreditApplied(invoices);
  const late = computeLateFeeOutstandingDue(invoices);
  const allocation_gap = Math.max(0, cash - allocated - verified - refundsOut);
  const unallocated_cash = allocation_gap;
  const tally =
    tallyLedgerBalance == null || !Number.isFinite(Number(tallyLedgerBalance))
      ? null
      : Math.round(Number(tallyLedgerBalance));

  let status = 'matched';
  let status_label = 'Balances look consistent';
  if (pricing_overage > 0 && open > 0) {
    status = 'pricing_mismatch';
    status_label =
      'Invoice schedule exceeds closed sale — use price-correction credit note, not a new payment';
  } else if (verified > 0 && open > 0) {
    status = 'unallocated_credit';
    status_label = 'Verified cash credit available — apply to open invoices';
  } else if (allocation_gap > 0 && open > 0) {
    status = 'allocation_gap';
    status_label =
      'Cash received exceeds allocations — review Payments / park credit';
  } else if (netToCollect > 0) {
    status = 'open_balance';
    status_label = 'Net amount to collect remains';
  } else if (tally != null && tally === 0 && open === 0) {
    status = 'matched';
    status_label = 'Matched with Tally ledger balance 0';
  }

  return {
    closed_sale: Math.round(base),
    adjusted_total: adjusted,
    schedule_total: schedule,
    net_schedule,
    pricing_overage,
    cash_received: cash,
    cash_allocated_to_invoices: allocated,
    allocated_paid: allocated,
    open_invoice_balance: open,
    net_amount_to_collect: netToCollect,
    late_fee_due: late,
    credit_balance: Math.max(0, verified + legacyUnverified),
    cash_credit_balance: verified,
    verified_cash_credit: verified,
    legacy_unverified_credit: legacyUnverified,
    commercial_credit_applied: commercialCredit,
    unallocated_cash,
    allocation_gap,
    tally_ledger_balance: tally,
    status,
    status_label
  };
}

export function defaultPriceCorrectionRemarks(closedSale, scheduleTotal) {
  const sale = new Intl.NumberFormat('en-IN').format(finiteNumber(closedSale));
  const sched = new Intl.NumberFormat('en-IN').format(finiteNumber(scheduleTotal));
  return `Historical invoice schedule (₹${sched}) exceeded closed sale (₹${sale}). Price-correction credit note — not a new payment.`;
}

/** Amount shown in the table "Late Fee" column for one invoice. */
export function invoiceLateFeeColumnAmount(invoice) {
  if (!isActiveInvoice(invoice) || isCreditNoteInvoice(invoice)) return 0;
  if (isLateFeeInvoice(invoice)) {
    // Late-fee invoices store the fee in `amount`, not late_fees
    return Math.max(
      0,
      finiteNumber(invoice?.amount) +
        finiteNumber(invoice?.late_fees) -
        finiteNumber(invoice?.paid_amount) -
        finiteNumber(invoice?.paid_late_fees)
    );
  }
  return Math.max(
    0,
    finiteNumber(invoice?.late_fees) - finiteNumber(invoice?.paid_late_fees)
  );
}

/** Safe rupee label — never "₹NaN". */
export function formatInrAmount(value) {
  const n = finiteNumber(value, 0);
  return `₹${new Intl.NumberFormat('en-IN').format(n)}`;
}
